<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('school.academic.operation.classOperation.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('school.academic.operation.classOperation.pageTitle'),
            href: '/class-operations'
          },
          { text: $t('create'), active: true }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <form class="form-horizontal">
          <h1 class="section-divider">
            {{ $t('school.academic.operation.classOperation.academicStudy') }}
          </h1>
          <div class="row mb-3">
            <div class="col-sm-12 col-lg-6 col-md-12">
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label">
                  {{
                    $t('school.academic.operation.classOperation.academicYear')
                  }}
                  <span class="text-danger">*</span>
                </label>
                <div class="col-md-7">
                  <a-select
                    style="width: 100%"
                    v-model.number="model.ac_year_id"
                    :options="ac_year_list"
                    @change="acYearChange"
                    :placeholder="$t('select')"
                  ></a-select>
                  <div class="validate" v-if="errors.has('ac_year_id')">
                    {{ errors.first('ac_year_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label">
                  {{
                    $t('school.academic.operation.classOperation.academicTerm')
                  }}
                  <span class="text-danger">*</span>
                </label>
                <div class="col-md-7">
                  <ts-search-select
                    v-model.number="model.ac_term_id"
                    :options="termLists"
                    @change="getMainAndSub"
                  ></ts-search-select>
                  <div class="validate" v-if="errors.has('ac_term_id')">
                    {{ errors.first('ac_term_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label">
                  {{
                    $t('school.academic.operation.classOperation.mainProgram')
                  }}
                  <i class="fa fa-spinner spin" v-if="filterSub"></i>
                </label>
                <div class="col-md-7">
                  <select class="form-control" disabled v-model="model.acp_id">
                    <option
                      v-for="(main, index) in mainProgram"
                      :key="index"
                      :value="main.value"
                    >
                      {{ main.text }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label">
                  {{
                    $t('school.academic.operation.classOperation.subProgram')
                  }}
                  <i class="fa fa-spinner spin" v-if="filterSub"></i>
                </label>
                <div class="col-md-7">
                  <select class="form-control" disabled v-model="model.acps_id">
                    <option
                      v-for="(sub, index) in subProgram"
                      :key="index"
                      :value="sub.value"
                    >
                      {{ sub.text }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label">
                  {{ $t('school.academic.operation.classOperation.level') }}
                  <i class="fa fa-spinner spin" v-if="filterLevel"></i>
                </label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    v-model="model.level_id"
                    :class="{ 'is-invalid': errors.has('level_id') }"
                  >
                    <option :value="null">{{ $t('select') }}</option>
                    <option
                      v-for="(level, index) in levelResource"
                      :key="index"
                      :value="level.level_id"
                    >
                      {{ level.level_name_foreign }}
                    </option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.has('level_id')">
                    {{ errors.first('level_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.grade') }}
                </label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    v-model="model.grade_id"
                    :class="{ 'is-invalid': errors.has('grade_id') }"
                    @change="onGetSubject"
                  >
                    <option :value="null">{{ $t('select') }}</option>
                    <option
                      v-for="(grade, index) in grades"
                      :key="index"
                      :value="grade.grade_id"
                    >
                      {{ grade.grade_name_foreign }}
                    </option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.has('grade_id')">
                    {{ errors.first('grade_id') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-sm-12 col-lg-6 col-md-12">
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.year') }}
                </label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    v-model="model.academic_year"
                    :class="{ 'is-invalid': errors.has('academic_year') }"
                    ref="academic_year"
                    @change="onGetSubject"
                  >
                    <option
                      :value="null"
                      disabled
                      selected
                      style="display: none"
                    >
                      {{ $t('select') }}
                    </option>
                    <option
                      v-for="(year, index) in academic_years"
                      :value="year"
                      :key="index"
                    >
                      {{ year }}
                    </option>
                  </select>
                  <div
                    class="invalid-feedback"
                    v-if="errors.has('academic_year')"
                  >
                    {{ errors.first('academic_year') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.semester') }}
                </label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    v-model="model.academic_term"
                    :class="{ 'is-invalid': errors.has('academic_term') }"
                    @change="onGetSubject"
                  >
                    <option
                      :value="null"
                      disabled
                      selected
                      style="display: none"
                    >
                      {{ $t('select') }}
                    </option>
                    <option
                      v-for="(term, index) in academic_terms"
                      :value="term"
                      :key="index"
                    >
                      {{ term }}
                    </option>
                  </select>
                  <div
                    class="invalid-feedback"
                    v-if="errors.has('academic_term')"
                  >
                    {{ errors.first('academic_term') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{
                    $t('school.academic.operation.classOperation.subjectName')
                  }}
                </label>
                <div class="col-md-7">
                  <ts-loading-banner :loading="fetchSubject">
                    <select
                      class="form-control"
                      :class="{ 'is-invalid': errors.has('subject_id') }"
                      v-model="model.subject_id"
                      @change="onChangeSubject($event)"
                    >
                      <option :value="null">{{ $t('select') }}</option>
                      <option
                        v-for="(subject, index) in subjectResource"
                        :key="index"
                        :value="subject.subject_id"
                      >
                        {{
                          subject.subject_name_foreign +
                          ' | ' +
                          subject.subject_code
                        }}
                      </option>
                    </select>
                  </ts-loading-banner>
                  <div class="invalid-feedback" v-if="errors.has('subject_id')">
                    {{ errors.first('subject_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.className') }}
                </label>
                <div class="col-md-7">
                  <input
                    v-model.trim="model.class_name"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('class_name') }"
                    ref="class_name"
                  />
                  <div class="invalid-feedback" v-if="errors.has('class_name')">
                    {{ errors.first('class_name') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label">
                  {{
                    $t('school.academic.operation.classOperation.description')
                  }}
                </label>
                <div class="col-md-7">
                  <textarea
                    v-model="model.class_desc"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('class_desc') }"
                    rows="1"
                  ></textarea>
                  <div class="invalid-feedback" v-if="errors.has('class_desc')">
                    {{ errors.first('class_desc') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">{{
                  $t('school.academic.operation.classOperation.weekdayType')
                }}</label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    v-model="model.weekday_type_id"
                    :class="{ 'is-invalid': errors.has('weekday_type_id') }"
                    @change="() => (model.ac_st_id = null)"
                  >
                    <option :value="null">{{ $t('select') }}</option>
                    <option
                      v-for="(weekday_type, index) in weekday_types"
                      :value="weekday_type.id"
                      :key="index"
                    >
                      {{ weekday_type.weekday_type }}
                      <span>{{
                        weekday_type.is_required_study_time
                          ? ' (Required study time: No)'
                          : ' (Required study time: Yes)'
                      }}</span>
                    </option>
                  </select>
                  <div
                    class="invalid-feedback"
                    v-if="errors.has('weekday_type_id')"
                  >
                    {{ errors.first('weekday_type_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label
                  class="col-md-4 form-col-label control-label"
                  :class="{ required: isRequiredStudyTime }"
                >
                  {{
                    $t('school.academic.operation.classOperation.studyTime')
                  }}</label
                >
                <div class="col-md-7">
                  <a-select
                    v-model="model.ac_st_id"
                    :options="studyTimes"
                    :placeholder="$t('select')"
                    :class="{ 'is-invalid': errors.has('ac_st_id') }"
                  >
                  </a-select>
                  <div class="validate" v-if="errors.has('ac_st_id')">
                    {{ errors.first('ac_st_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h1 class="section-divider">
            {{ $t('school.academic.operation.classOperation.moreInfo') }}
          </h1>
          <div class="row mb-3">
            <div class="col-sm-12 col-lg-6 col-md-12">
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.building') }}
                  <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                </label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    v-model="model.building_id"
                    :class="{ 'is-invalid': errors.has('building_id') }"
                  >
                    <option :value="null">{{ $t('select') }}</option>
                    <option
                      v-for="(b, index) in building"
                      :key="index"
                      :value="b.building_id"
                    >
                      {{ b.building_name }}
                    </option>
                  </select>
                  <div
                    class="invalid-feedback"
                    v-if="errors.has('building_id')"
                  >
                    {{ errors.first('building_id') }}
                  </div>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.room') }}
                </label>
                <div class="col-md-7">
                  <select
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('room_id') }"
                    v-model="model.room_id"
                  >
                    <option value>{{ $t('select') }}</option>
                    <option
                      v-for="(room, index) in getRooms"
                      :key="index"
                      :value="room.room_id"
                    >
                      {{ room.room_name }}
                    </option>
                  </select>
                  <div class="invalid-feedback" v-if="errors.has('room_id')">
                    {{ errors.first('room_id') }}
                  </div>
                </div>
              </div>
            </div>
            <div class="col-sm-12 col-lg-6 col-md-12">
              <div class="form-group row">
                <label class="col-md-4 form-col-label control-label required">
                  {{ $t('school.academic.operation.classOperation.lecturer') }}
                  <i class="fa fa-spinner spin" v-if="waitingFetch"></i>
                </label>
                <div class="col-md-7">
                  <ts-search-select
                    v-model="model.teacher_id"
                    :options="teacherLists"
                  ></ts-search-select>
                  <div class="validate" v-if="errors.has('teacher_id')">
                    {{ errors.first('teacher_id') }}
                  </div>
                </div>
              </div>
              <div class="row form-group">
                <label class="col-md-4 form-col-label control-label">
                  {{ $t('school.academic.operation.classOperation.intake') }}
                </label>
                <div class="col-md-7">
                  <input type="text" class="form-control" v-model="model.intake"/>
                </div>
              </div>
            </div>
          </div>
          <h1 class="section-divider">
            {{ $t('school.academic.operation.classOperation.requiredThesis') }}
          </h1>
          <div class="row mb-3">
            <div class="col-sm-12 col-lg-6 col-md-12">
              <div class="form-group row">
                <label class="col-md-4 col-form-label"></label>
                <div class="col-md-7">
                  <ts-checkbox v-model="model.required_thesis">
                    {{
                      $t(
                        'school.academic.operation.classOperation.requiredThesis'
                      )
                    }}
                  </ts-checkbox>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-12 text-right">
              <ts-button
                color="info"
                outline
                class="btn btn-default mr-2"
                @click="$router.push({ name: 'class-operation' })"
                >{{ $t('cancel') }}</ts-button
              >
              <ts-button
                outline
                color="primary"
                class="btn mr-2"
                @click.prevent="saveAddNew"
                :waiting="waiting_save_add_new"
                >{{ $t('save_add_new') }}</ts-button
              >
              <ts-button
                :waiting="waiting"
                class="btn btn-primary"
                @click.prevent="save"
                >{{ $t('save') }}</ts-button
              >
            </div>
          </div>
        </form>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'classOperationCreate',
  data () {
    return {
      model: {
        acp_id: null,
        acps_id: null,
        level_id: null,
        ac_year_id: null,
        ac_term_id: null,
        grade_id: null,
        room_id: null,
        teacher_id: null,
        academic_year: null,
        academic_term: null,
        subject_id: null,
        class_name: '',
        class_desc: '',
        building_id: null,
        ac_st_id: null,
        study_time: null,
        weekday_type_id: null,
        required_thesis: false,
        intake:null
      },
      waiting: false,
      waiting_save_add_new: false,
      waitingFetch: false,
      filterSub: false,
      filterLevel: false,
      fetchSubject: false,
      fetTeacherList: false,
      errors: new Errors(),
      levelResource: [],
      subjectResource: [],
      subResource: [],
      ac_year_list: [],
      ac_terms: [],
      loadTerm: false,
      study_times: []
    }
  },
  created () {
    this.fetchBeforeCreate()
  },
  computed: {
    ...mapState('classOperation', [
      'building',
      'categories',
      'academic_years',
      'academic_terms',
      'weekday_types'
    ]),
    teacherLists () {
      return this.$store.state.classOperation.teacherList.map(el => ({
        value: el.teacher_id,
        text: el.fname_foreign + ' ' + el.lname_foreign
      }))
    },
    termLists () {
      return _.sortBy(this.ac_terms, ['ac_status_id']).map(el => ({
        value: el.ac_term_id,
        text:
          el.term_name +
          (el.ac_status_id == 1
            ? ' ( Current ) '
            : el.ac_status_id == 2
            ? ' ( History ) '
            : ' ( Next ) ')
      }))
    },
    subProgram: {
      get () {
        return this.subResource.map(el => ({
          value: el.acps_id,
          text: el.acps_name_foreign
        }))
      }
    },
    mainProgram: {
      get () {
        if (this.subResource.length > 0) {
          this.model.acp_id = this.subResource[0].main_program.acp_id
          return [
            {
              value: this.subResource[0].main_program.acp_id,
              text: this.subResource[0].main_program.acp_name_foreign
            }
          ]
        }

        return []
      }
    },
    grades () {
      this.model.grade_id = null
      if (this.levelResource.length > 0) {
        let level = this.levelResource.filter(
          l => l.level_id === this.model.level_id
        )
        if (level.length > 0) {
          return level[0].grades
        }
      }

      return []
    },
    getRooms () {
      if (this.building.length > 0) {
        this.model.room_id = null
        let room = this.building.filter(
          r => r.building_id === this.model.building_id
        )
        if (room.length > 0) {
          return room[0].rooms
        }
      }

      return []
    },
    studyTimes () {
      if (this.model.weekday_type_id && this.study_times.length > 0) {
        return this.study_times
          .filter(s => s.weekday_type_id === this.model.weekday_type_id)
          .map(el => ({
            value: el.ac_st_id,
            label:
              el.study_time +
              ' | ' +
              el.session +
              (el.number_of_session
                ? ' | Session Order: ' +
                  moment(el.number_of_session, 'D').format('Do')
                : '')
          }))
      }

      return this.study_times.map(el => ({
        value: el.ac_st_id,
        label:
          el.study_time +
          ' | ' +
          el.session +
          (el.number_of_session
            ? ' | Session Order: ' +
              moment(el.number_of_session, 'D').format('Do')
            : '')
      }))
    },
    isRequiredStudyTime () {
      if (this.model.weekday_type_id && this.weekday_types.length > 0) {
        return !this.weekday_types.find(
          s => s.id === this.model.weekday_type_id
        ).is_required_study_time
      }

      return true
    }
  },
  methods: {
    onChangeSubject: function (e) {
      if (e.target.value) {
        this.model.class_name =
          e.target.options[e.target.options.selectedIndex].text
        this.$refs.class_name.focus()
      } else {
        this.model.class_name = ''
      }
    },
    async fetchBeforeCreate () {
      this.waitingFetch = true
      await this.$store
        .dispatch('classOperation/getYear')
        .then(response => {
          this.ac_year_list = response.data.map(el => ({
            value: el.ac_year_id,
            label: el.ac_year
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
      await this.$store
        .dispatch('classOperation/dataBeforeCreate')

        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waitingFetch = false
        })

      await this.$store
        .dispatch('classOperation/getStudyTime', {
          ac_st_id: this.model.ac_st_id,
          study_time: this.model.study_time
        })
        .then(response => {
          this.study_times = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waitingFetch = false
        })
    },
    getMainAndSub () {
      let filterSubInterm = this.ac_terms.filter(
        t => t.ac_term_id === this.model.ac_term_id
      )
      if (filterSubInterm.length) {
        this.filterSub = true
        this.model.ac_year_id = filterSubInterm[0].ac_year_id
        this.model.acps_id = filterSubInterm[0].acps_id
        this.$store
          .dispatch('classOperation/getMainAndSub', filterSubInterm[0].acps_id)
          .then(response => {
            this.subResource = response.data
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.filterSub = false
          })
      }
    },
    getLevelBySub (acps_id) {
      this.filterLevel = true
      this.model.level_id = null
      this.$store
        .dispatch('classOperation/getLevel', acps_id)
        .then(response => {
          this.levelResource = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.filterLevel = false
        })
    },
    onGetSubject () {
      this.subjectResource = []
      this.model.subject_id = null
      if (
        (this.model.grade_id == null && this.model.academic_year == null) ||
        this.model.academic_term == null
      )
        return
      this.fetchSubject = true
      this.$store
        .dispatch('classOperation/getSubject', {
          grade_id: this.model.grade_id,
          academic_year: this.model.academic_year,
          academic_term: this.model.academic_term
        })
        .then(response => {
          this.subjectResource = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.fetchSubject = false
        })
    },
    save () {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('classOperation/store', this.model)
        .then(response => {
          this.clearForm()
          this.waiting = false
          this.$toasted.success(response.message)
          this.$router.push({ name: 'class-operation' })
        })
        .catch(error => {
          this.waiting = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    saveAddNew () {
      this.errors = new Errors()
      this.waiting_save_add_new = true
      this.$store
        .dispatch('classOperation/store', this.model)
        .then(response => {
          this.clearForm()
          this.waiting_save_add_new = false
          this.$toasted.success(response.message)
        })
        .catch(error => {
          this.waiting_save_add_new = false
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
    },
    clearForm () {
      this.model = {
        acp_id: null,
        acps_id: null,
        level_id: null,
        ac_year_id: null,
        ac_term_id: null,
        grade_id: null,
        room_id: null,
        teacher_id: null,
        class_name: '',
        class_desc: '',
        building_id: null,
        weekday_type_id: null
      }
    },
    acYearChange () {
      this.model.acp_id = null
      this.model.acps_id = null
      this.model.ac_term_id = null
      this.loadTerm = true
      this.$store
        .dispatch('classOperation/getTerm', {
          ac_year_id: this.model.ac_year_id
        })
        .then(response => {
          this.ac_terms = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loadTerm = false
        })
    }
  },
  watch: {
    'model.acps_id': function (value) {
      if (value) {
        this.getLevelBySub(value)
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    if (_.isEmpty(to.params)) {
      this.$store.commit('classOperation/RESET_STATE')
      this.$store.commit('classOperation/SET_ALL_YEAR_FETCH', false)
      this.$store.commit('classOperation/SET_STUDY_TIMES', [])
    }
    next()
  }
}
</script>
